/// <amd-module name="Build/Run/RunLogsService" />
import { ApprovalUpdateParameters } from "Build/Client/Contracts/Approval";
import { IssueType, TaskResult, TimelineRecordState } from "Build/Client/Contracts/Build";
import { ArtifactsData, RunLinesData, RunLogsBaseData, RunLogsData, RunLogsRecordData, RunTimelineData } from "Build/Client/Contracts/DataProvider";
import { ResourcePipelinePermissions } from "Build/Client/Contracts/PipelinePermissions";
import { CheckSuiteUpdateParameter, UpdateAction } from "Build/Client/Contracts/PipelinesChecks";
import { IBuildLinkingService } from "Build/Client/Linking";
import { getApprovalClient } from "Build/Client/RestClient/Approval";
import { getPipelinePermissionsClient } from "Build/Client/RestClient/PipelinePermissions";
import { getPipelinesChecksClient } from "Build/Client/RestClient/PipelinesChecks";
import { NavConstants } from "Build/Common/Library/Constants";
import { handleError } from "Build/Common/Library/ErrorHandler";
import { IStatusIndicatorData, getTimelineStatusIndicatorData } from "Build/Common/Library/Indicator";
import { Security } from "Build/Common/Library/Security";
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import * as Resources from "Build/Run/Resources";
import { IRunExternalService, TestResults } from "Build/Run/RunExternalService";
import {
    CustomRunLogsData,
    IActionRecord,
    IBaseRecord,
    IJobPendingStatus,
    ILiveContentData,
    IPartialDataUpdate,
    IRecord,
    IRecordTreeItem,
    IRunLogsData,
    IRunLogsService,
    IRunLogsTreeData,
    IRunSelection,
    IStageRecord,
    ITaskSection,
    PreviousRunLogsRecordData
} from "Build/Run/RunLogsService.types";
import { ISignalRService } from "Build/Run/SignalRService";
import { TaskAgentJobRequest, TaskAgentRequestUpdateOptions } from "DistributedTask/Client/Contracts/TaskAgent";
import { ITaskAgentRestClient, getTaskAgentClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { escape } from "PipelinesUI/Utilities/Parser";
import { TestReportDataParser } from "Test/common-util/Common.Utils";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format, newGuid } from "VSS/Core/Util/String";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssHistoryService } from "VSS/Platform/History";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IStatusProps } from "VSSUI/Status";
import { AgoFormat, ago, duration } from "VSSUI/Utilities/Date";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";

//#region Common

const commandPrefix = (m: string) => `##[${m}]`;
const linkText = (link: string, text: string) => "<a class='is-link' href='" + link + "'" + ">" + text + "</a>";
const iconLog = (m: string) => `##[icon]${m} `;
const errorPrefix = commandPrefix("error");
const warningPrefix = commandPrefix("warning");
const groupPrefix = commandPrefix("group");
const endGroupPrefix = commandPrefix("endgroup");

const newLine = "\n";
const baseOrder = 10000;

const noLiveDataMinutes = 1;

const resiliencyCase1 = "1";
const resiliencyCase2 = "2";
const resiliencyCase3 = "3";

const emptyGuid = "00000000-0000-0000-0000-000000000000";

export class RunLogsService extends VssService implements IRunLogsService {
    //#region public

    /**
     * This will get observable for observing any live content changes.
     * Initialized with empty data to be subscribed to onMount
     */
    public get liveContent(): IReadonlyObservableValue<ILiveContentData | undefined> {
        return this._liveContentData;
    }

    /**
     * This will get all data for rendering logs and you would get notified everytime part of or whole data changed, if you are subscribing on this
     * Initialized when we get the data from dataprovider
     */
    public get logsData(): IReadonlyObservableValue<IRunLogsData> {
        return this._logsData;
    }

    /**
     * This will get all data for rendering tree and you would get notified everytime part of or whole data changed, if you are subscribing on this
     * Initialized when we get the data from dataprovider
     */
    public get treeData(): IReadonlyObservableValue<IRunLogsTreeData> {
        return this._treeData;
    }

    /**
     * Gets notified when partial data changes giving a chance for consumers to perform any other additional actions
     * Initialized when we get the data from dataprovider
     */
    public get partialData(): IReadonlyObservableValue<IPartialDataUpdate | undefined> {
        return this._partialData;
    }

    /**
     * Gets notified when selection changes giving a chance for consumers to perform any other additional actions
     * Initialized when we get the data from dataprovider
     */
    public get selection(): IReadonlyObservableValue<IRunSelection | undefined> {
        return this._selection;
    }

    /**
     * This will get raw data from dataprovider
     */
    public get rawData(): CustomRunLogsData {
        return this._rawData;
    }

    /**
     * Returns whether we are following tail
     */
    public get followTail(): boolean {
        return this._followTail;
    }

    public setFocused(_onFocus: boolean) {
        this._onFocus = _onFocus;
    };

    private _contributionService: IVssContributionService;
    private _historyService: IVssHistoryService;
    private _logsData: IReadonlyObservableValue<IRunLogsData>;
    private _treeData: IReadonlyObservableValue<IRunLogsTreeData>;
    private _partialData: IReadonlyObservableValue<IPartialDataUpdate | undefined>;
    private _selection: IReadonlyObservableValue<IRunSelection | undefined>;
    private _security: Security;
    private _permissionsStore: PermissionsStore;
    // a promise is when the task is completed
    private _content: { [key: string]: Promise<string> } = {};
    private _liveContent: { [key: string]: {content: string, startLine: number} } = {};
    private _jobStatus: { [key: string]: IJobPendingStatus } = {};
    private _phaseLog: { [key: string]: string } = {};
    private _jobTestData: { [key: string]: TestResults } = {};
    private _jobArtifactData: { [key: string]: ArtifactsData } = {};
    private _skipTestCall: { [key: string]: boolean } = {};
    private _skipArtifactsCall: { [key: string]: boolean } = {};
    private _jobCompletedCalls: { [key: string]: boolean } = {};
    private _url: { [key: string]: string } = {};
    private _followTail: boolean = true;
    private _onFocus: boolean = true;

    // We fake phase as jobs if there are no jobs yet, once we have jobs, the phase node is deleted, this tracks such id's and helps in ensuring selection
    private _resetPhaseSelection: string | undefined;

    // to notify listeners on latest live data
    private _liveContentData: IReadonlyObservableValue<ILiveContentData | undefined>;

    private _rawData: CustomRunLogsData;

    private _marker: number | undefined;

    private _expandedStages: { [key: string]: boolean } = {};

    // Tracks requests made
    private _resiliency: { [key: string]: boolean } = {};
    private _timer: TimerManagement;

    private _lastUpdate: Date = new Date();
    private _liveCheckTimer: number | undefined;

    private _dtClient: ITaskAgentRestClient;
    private _signalR: ISignalRService;
    private _extService: IRunExternalService;
    private _linking: IBuildLinkingService;

    private _refreshWhenCompleted: { [key: string]: boolean } = {};

    // TaskId to expanded state, we store this so that we can preserve this all the item
    private _expanded: { [key: string]: boolean } = {};

    // Continuation Token to boolean
    private _linesFetching: { [token: string]: boolean } = {};

    private _previousTimelines: RunLogsBaseData[] = [];
    private _previousTimelinesMap: { [stage: string]: { [timelineId: string]: number } } = {};
    private updatedSelection = false;
    //#endregion

    //#endregion

    private readonly _provider = "ms.vss-build-web.run-logs-data-provider";
    private readonly _poolPermissionProvider = "ms.vss-build-web.task-agent-pool-security-data-provider";
    private readonly _parallelismTagKey = "ParallelismTag";
    private readonly _queuePositionKey = "QueuePosition";
    private readonly _private = "Private";

    /**
     * Returns record's log URL
     */
    public getLogUrl(id: string): string | undefined {
        return this._url[id];
    }

    /**
     * Update check suite
     * @param checkId check run id
     * @param checkSuiteId check suite id
    */
    public bypassCheck(checkId: string, checkSuiteId: string) {
        const request = {
            action: UpdateAction.Bypass,
            checkId: checkId
        } as CheckSuiteUpdateParameter;

        return getPipelinesChecksClient(this.pageContext)
            .updateCheckSuite(request, this._rawData.projectId, checkSuiteId);
    }

    /**
     * Update approvals
     * @param approvals approvals
     */
    public updateApprovals(approvals: ApprovalUpdateParameters[]) {
        return getApprovalClient(this.pageContext).updateApprovals(approvals, this._rawData.projectId);
    }

    /**
     * Update approvals
     * @param approvals approvals
     */
    public rerunCheck(checkId: string, checkSuiteId: string) {
        const request = {
            action: UpdateAction.Rerun,
            checkId: checkId            
        } as CheckSuiteUpdateParameter;
        return getPipelinesChecksClient(this.pageContext).updateCheckSuite(request, this._rawData.projectId, checkSuiteId);
    }

    /**
     * Update permissions
     * @param permissions permissions data
     * @param resourceType resource type
     * @param resourceId resource id
     */
    public updatePermissions(
        permissions: ResourcePipelinePermissions,
        resourceType: string,
        resourceId: string
    ): Promise<void | ResourcePipelinePermissions> {
        return getPipelinePermissionsClient(this.pageContext)
            .updatePipelinePermisionsForResource(permissions, this._rawData.projectId, resourceType, resourceId)
            .catch(handleError);
    }

    /**
     * Gets latest data
     */
    public refresh() {
        this._initialize(true);
    }

    /**
     * Create a logs record
     * @param record record from dataprovider from which the logs record should be created
     */
    public createRecord(record: RunLogsRecordData, isJob: boolean, isTask: boolean): IRecord {
        const indicatorData: IStatusIndicatorData = getTimelineStatusIndicatorData(
            record.state,
            record.result,
            record.stateData && record.stateData.pendingChecks,
            record.stateData && !record.stateData.pendingDependencies
        );
        const statusProps: IStatusProps = indicatorData.statusProps;
        statusProps.ariaLabel = indicatorData.label;
        let adjusted;
        if (!record.refName || (record.refName && record.refName.indexOf("JobExtension") > -1 && statusProps.color === "success")) {
            adjusted = {
                ...statusProps,
                color: "system-task"
            } as IStatusProps;
        }
        return {
            name: record.name,
            id: record.id,
            statusProps: adjusted || statusProps,
            start: record.startTime,
            end: record.finishTime,
            isJob: isJob,
            isTask: isTask,
            issues: record.issues,
            workerName: record.workerName,
            state: record.state,
            result: record.result,
            percentComplete: record.percentComplete
        };
    }

    public createAttemptRecord(stage: string, name: string, isJob: boolean, isTask: boolean): IBaseRecord {
        return {
            name: name,
            id: stage + name,
            statusProps: undefined,
            isJob: isJob,
            isTask: isTask
        };
    }

    public createActionRecord(name: string, isJob: boolean, isTask: boolean, onClick: () => void): IActionRecord {
        return {
            name: name,
            id: newGuid(),
            statusProps: undefined,
            isJob: isJob,
            isTask: isTask,
            onClick: onClick
        };
    }

    /**
     * Creates a task section, this also updates URL for tasks
     * @param buildId buildId
     * @param record record from which the task section should be created
     */
    public createTask(buildId: number, record: RunLogsRecordData): ITaskSection {
        const key = record.id;
        this._updateUrl(buildId, key, record.logId);
        const taskRecord = this.createRecord(record, false, true);
        const hasLogs = !!this.getLogUrl(key) || !!this._liveContent[key] || (record.result === TaskResult.Skipped && !!record.message);
        const areLogsMissing = isFeatureFlagEnabled(this.pageContext, "Build.ShowNoLogsMessage") && !hasLogs && record.state == TimelineRecordState.Completed;

        // once we set expanded to true, we don't update later, this helps in keeping the expanded state as soon as the build completes
        if (this._expanded[key] == undefined || !this._expanded[key]) {
            this._expanded[key] = record.state === TimelineRecordState.InProgress;
        }

        return {
            key: key,
            title: record.name,
            isExpanded: this._expanded[key],
            getData: () => {
                if (record.state !== TimelineRecordState.Completed) {
                    const currentDataPromise = this._getData(key);
                    // this implies that step is running, but we have a logUrl set, this happens for agent-less steps
                    if (currentDataPromise) {
                        // make sure we refresh when it's completed so that it gets the new promise/content after it switches from live streaming
                        this._refreshWhenCompleted[key] = true;
                        return new Promise(async (resolve, reject) => {
                            const x = await currentDataPromise;
                            resolve(x + this._getLiveData(key));
                        });
                    }

                    return this._getLiveData(key);
                }

                if (record.result === TaskResult.Skipped) {
                    return record.message;
                }

                delete this._liveContent[key];

                // clear off promise to get new content
                if (this._refreshWhenCompleted[key]) {
                    delete this._refreshWhenCompleted[key];
                    delete this._content[key];
                }

                if (areLogsMissing) {
                    return Promise.resolve(Resources.NoLogsToShow);
                }

                return this._getData(key);
            },
            statusProps: getTimelineStatusIndicatorData(record.state, record.result).statusProps,
            hasData: hasLogs || areLogsMissing,
            payload: {
                jobId: record.parentId,
                stageId: (record as PreviousRunLogsRecordData).stageId,
                ...taskRecord
            },
            // hidden: make this a sectionless render in the logviewer since we only have the one section
            hidden: true
        };
    }

    /**
     * When a job is selected
     * @param jobId Job id to select
     */
    public selectJob(jobId: string) {
        const state = this._historyService.getState();

        delete state[NavConstants.line];
        delete state[NavConstants.task];
        delete state[NavConstants.stage];

        state[NavConstants.job] = jobId;

        // Set stage for previous attempts
        const job = this._getJobById(jobId);
        if (job && (job as PreviousRunLogsRecordData).stageId) {
            state[NavConstants.stage] = (job as PreviousRunLogsRecordData).stageId + "";
        }

        this._historyService.replaceState(state);
        // since we replaced state. we don't get notified, so explictly call to update selection
        this._updateSelection();
    }

    /**
     * When a task is selected
     * @param taskId task id to select
     */
    public selectTask(taskId: string) {
        const state = this._historyService.getState();
        delete state[NavConstants.line];
        delete state[NavConstants.task];
        delete state[NavConstants.stage];
        const task = this._getTaskById(taskId);
        if (task) {
            const job = this._getJobById(task.parentId);
            if (job) {
                state[NavConstants.job] = task.parentId;
            } else {
                // we only support one level of subtasks right now so if we have a
                // subtask, we only need to go up one level to find the associated job
                const parentTask = this._getTaskById(task.parentId);
                if (parentTask) {
                    state[NavConstants.job] = parentTask.parentId;
                }
            }
        }
        state[NavConstants.task] = taskId;

        // Set stage for previous attempts
        if (task && (task as PreviousRunLogsRecordData).stageId) {
            state[NavConstants.stage] = (task as PreviousRunLogsRecordData).stageId + "";
        }

        this._historyService.replaceState(state);
        // since we replaced state. we don't get notified, so explictly call to update selection
        this._updateSelection();
    }

    /**
     * Set to break into or out of following the tail of logs
     * @param follow whether we want to follow tail
     */
    public setFollowTail(follow: boolean) {
        this._followTail = follow;
    }

    /**
     * Store expanded stages
     * @param item item that's expanded
     */
    public expandStage(item: ITreeItem<IBaseRecord>) {
        this._expandedStages[item.data.id] = !!item.expanded;
    }

    /**
     * Updates live content from signalR
     */
    public onLiveContent(data: ILiveContentData) {
        if (data.timelineId !== this._rawData.timelineId) {
            return;
        }

        const currentJobId = this._selection.value && this._selection.value.jobId;
        const currentTaskId = this._selection.value && this._selection.value.taskId;
        this._lastUpdate = new Date();
        // older agents (pre-M134) won't send taskId...see PR #325455,
        if (data.taskId === emptyGuid) {
            // vote current running task as the one we want
            if (currentJobId) {
                for (let index = 0; index < this._rawData.tasks.length; index++) {
                    const task = this._rawData.tasks[index];
                    if (task.parentId === currentJobId && task.state === TimelineRecordState.InProgress) {
                        data.taskId = task.id;
                        break;
                    }
                }
            }
        }

        if (data.taskId !== emptyGuid) {
            if (currentJobId === data.jobId) {
                this._initJobPendingReason(this._getCurrentJob());
            }
            this._liveLogCheck(data);
            let existingContent = this._liveContent[data.taskId]?.content ?? "";
            existingContent = existingContent.length > 0 ? existingContent + "\n" : "";
            this._liveContent[data.taskId] = {content: existingContent + data.content, startLine: this._liveContent[data.taskId]?.startLine ?? 1};

            if (this._followTail && currentJobId === data.jobId && currentTaskId != data.taskId) {
                this.selectTask(data.taskId);
            }

            // notify only if we got data for the job we are looking at
            if (currentJobId === data.jobId) {
                (this._liveContentData as ObservableValue<ILiveContentData | undefined>).value = data;
            }
        }
    }

    /**
     * Call whenever timeline is live updated
     */
    public onLiveTimelineUpdate(timelineId: string, changeId: number, done?: () => void) {
        if (timelineId !== this._rawData.timelineId) {
            return;
        }

        if (!this._marker) {
            this._marker = changeId;
        }

        if (this._marker < changeId) {
            this._marker = changeId;
        }

        this._lastUpdate = new Date();

        this._initNoLiveCheck();

        this._fetchTimeline(this._marker).then(fetchedData => {
            if (fetchedData) {
                if ((this._marker as number) < fetchedData.changeId) {
                    this._marker = fetchedData.changeId;
                }

                const updatedStageIds: string[] = [];
                const updatedPhaseIds: string[] = [];
                const updatedJobIds: string[] = [];
                const updatedTaskIds: string[] = [];
                const newStageIds: string[] = [];
                const newPhaseIds: string[] = [];
                const newJobIds: string[] = [];
                const newTaskIds: string[] = [];

                this._merge(fetchedData.stages, this._rawData.stages, this._rawData.stageMap, updatedStageIds, newStageIds);
                this._merge(fetchedData.phases, this._rawData.phases, this._rawData.phaseMap, updatedPhaseIds, newPhaseIds, x => {
                    this._updateOrder(x, baseOrder, this._rawData.stageMap, this._rawData.stages);
                });

                this._merge(fetchedData.jobs, this._rawData.jobs, this._rawData.jobMap, updatedJobIds, newJobIds, x => {
                    this._updateOrder(x, baseOrder * 10, this._rawData.phaseMap, this._rawData.phases);
                    if (x.name === "Job") {
                        // If the job name is the default "Job", use the phase name in its
                        // place to account for builds predating M151 when the job names
                        // were improved to include the phase name
                        const phase = this._rawData.phases[this._rawData.phaseMap[x.parentId]];
                        if (phase && phase.name && phase.name !== x.name && phase.name.length > 0) {
                            x.name = phase.name;
                        }
                    }
                });
                this._merge(fetchedData.tasks, this._rawData.tasks, this._rawData.taskMap, updatedTaskIds, newTaskIds, x => {
                    this._updateOrder(x, baseOrder * 100, this._rawData.jobMap, this._rawData.jobs);
                });

                this._normalizeRawData();

                // for tree: for now let's just update the whole tree everytime we get an update, this isn't hot path, tree is simple, optimizing it demands more code that isn't probably beneficial
                this._updateTreeData();

                this._ensureSelection();

                // for logs: update tasks for current job
                const currentJob = this._getCurrentJob();
                const currentTask = this._getCurrentTask();
                let updateCurrentTask;
                let updateCurrentJob;

                if (currentTask) {
                    updateCurrentTask = currentTask && (updatedTaskIds.indexOf(currentTask.id) > -1 || newTaskIds.indexOf(currentTask.id) > -1);
                }
                if (currentJob) {
                    updateCurrentJob = updatedJobIds.indexOf(currentJob.id) > -1 || newJobIds.indexOf(currentJob.id) > -1;
                }
                if (currentJob && ((updateCurrentJob && this._followTail) || updateCurrentTask)) {
                    this._updateLogsData(this._rawData.buildId, currentJob.id, currentTask ? currentTask.id : "", this._getTasks());

                    if (currentJob.state === TimelineRecordState.Completed && !this._jobCompletedCalls[currentJob.id]) {
                        this._jobCompletedCalls[currentJob.id] = true;
                        this._updateArtifacts(currentJob, true);
                        this._updateTestData(currentJob, this._rawData.phases[this._rawData.phaseMap[currentJob.parentId]], true);
                    }
                }

                done && done();
            }
        });
    }

    public onLiveRequestUpdate(poolId: number, request: TaskAgentJobRequest, isNewRequest?: boolean) {
        const currentJob = this._getCurrentJob(request.jobId);
        if (currentJob) {
            const requestData = this._jobStatus[currentJob.id];
            let update = false;
            if (requestData) {
                if (requestData.poolId === request.poolId && requestData.requestId === request.requestId) {
                    // got update for the request we are interested it, fetch latest status
                    update = true;
                }
            } else if (isNewRequest) {
                // we didn't have any existing request for this yet and we got some new request assigned!
                const queueData = this._rawData.jobQueueData[currentJob.id];
                if (queueData) {
                    if (request.poolId === queueData.poolId) {
                        // new request is for the pool we are interested in...this can change the status message, so fetch
                        update = true;
                    }
                } else {
                    // we don't have queue data... let's see if we can get updated queue data from data provider
                    const params: { [key: string]: any } = {};
                    params[NavConstants.buildId] = this._rawData.buildId + "";
                    params[NavConstants.update] = NavConstants.jobQueueData;
                    this._contributionService.getDataAsync<RunLogsData>(this._provider, params, true).then(runLogsData => {
                        if (runLogsData) {
                            const rawData = runLogsData as CustomRunLogsData;

                            this._rawData.jobQueueData = rawData.jobQueueData || this._rawData.jobQueueData || {};

                            const queueData = this._rawData.jobQueueData[currentJob.id];
                            if (queueData && request.poolId === queueData.poolId) {
                                this._initJobPendingReason(currentJob);
                            }
                        }
                    });
                }
            }

            if (update) {
                this._initJobPendingReason();
            }
        }
    }

    public onLiveAgentStatusUpdate(poolId: number, agentId: number) {
        const currentJob = this._getCurrentJob();
        if (currentJob) {
            const requestData = this._jobStatus[currentJob.id];
            if (requestData && requestData.matchedAgents.indexOf(agentId) > -1) {
                // got update for the agents we are interested in, fetch latest status
                this._initJobPendingReason();
            }
        }
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._timer = new TimerManagement();
        this._liveLogCheck = this._timer.throttle(this._liveLogCheck, 600);
        this._partialData = new ObservableValue<IPartialDataUpdate | undefined>(undefined);
        this._selection = new ObservableValue<IRunSelection | undefined>(undefined);
        this._liveContentData = new ObservableValue<ILiveContentData | undefined>(undefined);
        this._selection.subscribe(this._onSelectionChange);
        this._contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._signalR = this.pageContext.getService<ISignalRService>("ISignalRService");
        this._extService = this.pageContext.getService<IRunExternalService>("IRunExternalService");
        this._linking = this.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        this._historyService.subscribe(this._updateNavigation);
        this._dtClient = getTaskAgentClient(this.pageContext);
        this._initialize();
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        this._selection.unsubscribe(this._onSelectionChange);
        this._historyService.unsubscribe(this._updateNavigation);
        this._timer.dispose();
    }

    public getCurrentJobId(): string | undefined {
        const currentJob = this._getCurrentJob();
        if (currentJob) {
            return currentJob.refName;
        }
    }

    public getCurrentPhaseId(): string | undefined {
        const currentJob = this._getCurrentJob();
        const currentPhase = currentJob && this._rawData.phases[this._rawData.phaseMap[currentJob.parentId]];
        if (currentPhase) {
            return currentPhase.refName;
        }
    }

    private _getCurrentStage(): RunLogsRecordData | undefined {
        const currentJob = this._getCurrentJob();
        if (currentJob) {
            return this._getStageRecord(currentJob);
        }
    }

    public getCurrentStageId(): string | undefined {
        const currentStage = this._getCurrentStage();
        if (currentStage) {
            return currentStage.refName;
        }
    }

    public getCurrentStageName(): string | undefined {
        const currentStage = this._getCurrentStage();
        if (currentStage) {
            return currentStage.originalName;
        }
    }

    public bumpJobPriority(jobId: string) {
        const poolId = this._jobStatus[jobId].poolId;
        const requestId = this._jobStatus[jobId].requestId;
        this._dtClient
            .updateAgentRequest(
                { requestId: requestId } as TaskAgentJobRequest,
                poolId,
                requestId,
                emptyGuid,
                TaskAgentRequestUpdateOptions.BumpRequestToTop
            )
            .then(x => {
                this._initJobPendingReason();
            });
    }

    public canPriorityBeBumped(jobId: string): boolean {
        const jobData = this._jobStatus[jobId];
        return (
            isFeatureFlagEnabled(this.pageContext, "DistributedTask.BumpAgentRequestPriority", false) &&
            jobData &&
            this._security.hasManageAgentPoolPermission(jobData.poolId) &&
            jobData.data[this._queuePositionKey] !== undefined &&
            parseInt(jobData.data[this._queuePositionKey]) > 1
        );
    }

    //#endregion

    //#region Private

    private _updateNavigation = () => {
        this._updateSelection();
    };

    private _ensureSelection() {
        if (this._resetPhaseSelection) {
            const job = (this._rawData.jobs || []).find(x => x.parentId === this._resetPhaseSelection);
            if (job) {
                this.selectJob(job.id);
                this._resetPhaseSelection = undefined;
            }
        }
    }

    private _initialize(refresh: boolean = false) {
        const data = refresh ? undefined : this._contributionService.getData<RunLogsData>(this._provider);
        if (data) {
            this._permissionsStore = new PermissionsStore({ pageContext: this.pageContext });
            this._security = new Security({
                projectId: data.projectId,
                store: this._permissionsStore
            });

            this._initializeData(data);
        } else if (this._rawData) {
            const params: { [key: string]: string } = {};
            params[NavConstants.buildId] = this._rawData.buildId + "";
            this._contributionService.getDataAsync<RunLogsData>(this._provider, params, true).then(x => {
                x && this._initializeData(x);
            });
        }
    }

    private _initializeData(data: RunLogsData) {
        this._rawData = data as CustomRunLogsData;

        const navigationState = this._historyService.getState();
        this._rawData.jobs.map(async job => {
            await job.previousAttempts?.forEach(async attempt => {
                await this._loadAndUpdateAttempts({} as RunLogsRecordData, attempt?.timelineId);
                if (navigationState[NavConstants.job] === attempt.recordId && this._previousTimelines) {
                    this._updateSelection(navigationState);
                }
            });
        });

        this._normalizeRawData();

        console.log(this._rawData);

        this._initNoLiveCheck();

        // change of selection will initialize jobData
        this._updateSelection(navigationState);
        this._updateSelection();

        this._updateTreeData();
    }

    private _fetchTimeline(changeId: number, timelineId?: string, fullTimeline?: boolean) {
        const params: { [key: string]: string } = {};
        params[NavConstants.buildId] = this._rawData.buildId + "";
        params[NavConstants.timelineId] = timelineId ? timelineId : this._rawData.timelineId;
        params[NavConstants.changeId] = changeId + "";
        params[NavConstants.fullTimeline] = !!fullTimeline + "";
        return this._contributionService.getDataAsync<RunTimelineData>("ms.vss-build-web.run-timeline-data-provider", params);
    }

    private async _loadAndUpdateAttempts(stage: RunLogsRecordData, timelineId?: string) {
        const params: { [key: string]: string } = {};
        params[NavConstants.buildId] = this._rawData.buildId.toString();
        if (stage.refName) {
            params[NavConstants.stageName] = stage.refName;
        }
        if (timelineId) {
            params["timelineId"] = timelineId;
        }
        const attemptTimelines = await this._contributionService.getDataAsync<RunLogsBaseData[]>(
            "ms.vss-build-web.run-attempts-data-provider",
            params
        );

        if (attemptTimelines) {
            if (stage.id) {
                attemptTimelines.forEach(timeline => {
                    timeline && this._addPreviousTimeline(timeline, stage);
                });
            } else {
                attemptTimelines &&
                    attemptTimelines.forEach(timeline => {
                        timeline.stages.forEach(stageTimeline => this._addPreviousTimeline(timeline, stageTimeline));
                    });
            }
        }
    }

    private _fetchLines(jobId: string, stepId: string, continuationToken?: string) {
        const params: { [key: string]: string } = {};
        params[NavConstants.planId] = this._rawData.planId;
        params[NavConstants.timelineId] = this._rawData.timelineId;
        params[NavConstants.jobId] = jobId;
        params[NavConstants.stepId] = stepId;
        if (continuationToken) {
            params[NavConstants.continuationToken] = continuationToken;
        }

        return this._contributionService.getDataAsync<RunLinesData>("ms.vss-build-web.run-lines-data-provider", params);
    }

    private _normalizeRawData() {
        let updated = false;
        this._rawData.stages = (this._rawData.stages || []).sort(this._sortRecords);
        this._rawData.phases = (this._rawData.phases || []).sort(this._sortRecords);
        this._rawData.jobs = (this._rawData.jobs || []).sort(this._sortRecords);
        this._rawData.tasks = (this._rawData.tasks || []).sort(this._sortRecords);

        this._rawData.stageMap = this._rawData.stageMap || {};
        this._rawData.phaseMap = this._rawData.phaseMap || {};
        this._rawData.jobMap = this._rawData.jobMap || {};
        this._rawData.taskMap = this._rawData.taskMap || {};

        this._rawData.jobQueueData = this._rawData.jobQueueData || {};

        this._updateMaps();

        this._rawData.stages.forEach(stage => {
            stage.previousAttempts = stage.previousAttempts.sort((a, b) => b.attempt - a.attempt);
        });

        // we inject a phase that has no jobs as a job, let's make sure we clean them up if the phase already has atleast one job
        // this is required since we merge records are live updates happen, so we could get a job for a phase after the initial set of data
        const phaseHasJobs = new Set<string>();
        this._rawData.jobs.forEach(x => {
            if (x.id !== x.parentId) {
                phaseHasJobs.add(x.parentId);
            }
        });

        const currentJobs = this._rawData.jobs.slice();

        phaseHasJobs.forEach(j => {
            // find indexes for jobs that have same as phaseId we accumulated and delete them
            const index = this._rawData.jobMap[j];
            const job = currentJobs[index];
            if (job && job.id === job.parentId) {
                if (this._selection.value && this._selection.value.jobId === job.id) {
                    this._resetPhaseSelection = job.id;
                }
                delete this._rawData.jobMap[j];
                currentJobs.splice(index, 1);
                updated = true;

                // After splicing, map is now outdated. Update before processing more phases
                currentJobs.forEach((x, index) => {
                    this._rawData.jobMap[x.id] = index;
                });
            }
        });

        if (updated) {
            this._rawData.jobs = currentJobs.sort(this._sortRecords);
            this._updateMaps();
        }
    }

    private _updateMaps() {
        // update maps
        this._rawData.stages.forEach((x, index) => {
            this._rawData.stageMap[x.id] = index;
        });
        this._rawData.phases.forEach((x, index) => {
            this._rawData.phaseMap[x.id] = index;
        });
        this._rawData.jobs.forEach((x, index) => {
            this._rawData.jobMap[x.id] = index;
        });
        this._rawData.tasks.forEach((x, index) => {
            this._rawData.taskMap[x.id] = index;
        });
    }

    private _merge(
        from: RunLogsRecordData[],
        to: RunLogsRecordData[],
        map: { [key: string]: number },
        updatedResult: string[],
        newResult: string[],
        updateData?: (x: RunLogsRecordData) => void
    ) {
        (from || []).forEach(x => {
            if (updateData) {
                updateData(x);
            }

            const currentIndex = map[x.id];
            if (currentIndex !== undefined) {
                to[currentIndex] = x;
                updatedResult.push(x.id);
                map[x.id] = currentIndex;
            } else {
                to.push(x);
                newResult.push(x.id);
            }
        });
    }

    private _initNoLiveCheck() {
        if (this._isDone()) {
            if (this._liveCheckTimer) {
                this._timer.clearInterval(this._liveCheckTimer);
            }
        } else if (!this._liveCheckTimer) {
            this._liveCheckTimer = this._timer.setInterval(this._noLiveCheck, noLiveDataMinutes * 60000);
        }
    }

    private _isDone() {
        let completed = true;
        for (let index = 0; index < this._rawData.stages.length; index++) {
            const stage = this._rawData.stages[index];
            if (stage.state !== TimelineRecordState.Completed) {
                completed = false;
                break;
            }
        }
        return completed;
    }

    private _isCurrentTaskDone() {
        const currentTask = this._getCurrentTask();
        return currentTask?.state === TimelineRecordState.Completed;
    }

    private _isCurrentStageDone() {
        const currentStage = this._getCurrentStage();
        return currentStage?.state === TimelineRecordState.Completed;
    }

    private _noLiveCheck = () => {
        if (this._isDone()) {
            this._timer.clearInterval(this._liveCheckTimer as number);
            return;
        }

        if (this._isCurrentTaskDone() || this._isCurrentStageDone()) {
            return;
        }
        
        // if tab is not focused then no update will be done
        if(!this._onFocus) {
            return;
        }

        const currentTime = new Date();
        const diff = new Date(currentTime.getTime() - this._lastUpdate.getTime());
        if (diff.getMinutes() >= noLiveDataMinutes) {
            // we got no updates yet, refresh!
            console.info(`#resiliency${resiliencyCase3}`);
            this._initialize(true);
        }
    };

    private _liveLogCheck = (data: ILiveContentData) => {
        const currentJob = this._getCurrentJob();
        if (!currentJob || currentJob.id !== data.jobId) {
            return;
        }

        // #resiliency: when we start getting console events, but job records are not updated yet
        const job = this._rawData.jobs[this._rawData.jobMap[data.jobId]];
        let caseName = "";
        if (!job || !job.startTime) {
            caseName = resiliencyCase1;
        }

        // For job level console log, we get taskId same as jobId
        if (data.taskId !== data.jobId) {
            const task = this._rawData.tasks[this._rawData.taskMap[data.taskId]];
            if (!task || !task.startTime) {
                caseName = resiliencyCase2;
            }
        }

        if (caseName) {
            if (!this._resiliency[caseName]) {
                console.info(`#resiliency${caseName}`, data, this._rawData.jobs, this._rawData.tasks);
                this._resiliency[caseName] = true;
                this.onLiveTimelineUpdate(this._rawData.timelineId, 0, () => {
                    delete this._resiliency[caseName];
                });
            }
        }
    };

    private _updateOrder(record: RunLogsRecordData, factor: number, map: { [key: string]: number }, records: RunLogsRecordData[]) {
        const parentIndex = map && map[record.parentId];
        if (parentIndex !== undefined) {
            record.order = record.originalOrder + factor * records[parentIndex].originalOrder;
        }
        return record;
    }

    private _getCurrentJob(jobId?: string) {
        const currentJobId = jobId || (this._selection.value && this._selection.value.jobId);
        if (currentJobId) {
            return this._getJobById(currentJobId);
        }
    }

    private _getCurrentTask(taskId?: string) {
        const currentTaskId = taskId || (this._selection.value && this._selection.value.taskId);
        if (currentTaskId) {
            return this._getTaskById(currentTaskId);
        }
    }

    /**
     * Creates the job section, Id of this section would be the current Job Id
     * @param currentJob
     */
    private _getJobSection(currentJob: RunLogsRecordData): ITaskSection | null {
        const currentJobRecord = this.createRecord(currentJob, true, false);
        let jobInfoText = "";
        // add any issues
        jobInfoText += (currentJobRecord.issues || []).map(x => {
            const safeMessage = escape(x.message);
            let issueText = "";
            if (x.type === IssueType.Error) {
                issueText += errorPrefix + safeMessage;
            }
            if (x.type === IssueType.Warning) {
                issueText += warningPrefix + safeMessage;
            }

            return issueText + newLine;
        });

        let start = currentJobRecord.start;
        if (!start && currentJob.state !== TimelineRecordState.Pending) {
            // if there's no start time but the job is not pending, let's do best effort and get start time from it's parents
            //  we typically see this for server jobs
            const phase = this._rawData.phases[this._rawData.phaseMap[currentJob.parentId]];
            if (phase) {
                start = phase.startTime;
                if (!start) {
                    const stage = this._rawData.stages[this._rawData.stageMap[phase.parentId]];
                    if (stage) {
                        start = stage.startTime;
                    }
                }
            }
        }

        // add information
        // 1. pool data
        const queueData = this._rawData.jobQueueData[currentJob.id];
        if (queueData) {
            // 1. a) pool name and url
            const link = this._getPoolUrl(queueData.id);
            const poolName = queueData.poolName || queueData.name || queueData.poolId + "";
            jobInfoText += format(Resources.JobInfoPool, linkText(link, escape(poolName)));
            jobInfoText += newLine;
            // 1. b) image name
            if (queueData.imageName) {
                jobInfoText += format(Resources.JobInfoImage, escape(queueData.imageName));
                jobInfoText += newLine;
            }
        }
        // 2. job queue time
        const jobStatus = this._jobStatus[currentJob.id];
        if (queueData && jobStatus) {
            const vssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
            const vssPageData = vssPageService.getData();

            const isHostedService = vssPageData === undefined || vssPageData.isHosted;

            if (isHostedService) {
                // default to private pool if parallelism tag isn't available
                const parallelismTag = jobStatus.data[this._parallelismTagKey] || this._private;
                const link = this._getParallelJobsUrl(queueData.isHosted, parallelismTag);
                jobInfoText += format(
                    Resources.JobInfoQueued,
                    ago(jobStatus.queueTime, AgoFormat.Extended),
                    linkText(link, Resources.ParallelJobsLinkText)
                );
                jobInfoText += newLine;
            } else {
                jobInfoText += format(Resources.JobInfoQueuedOnPrem, ago(jobStatus.queueTime, AgoFormat.Extended));
                jobInfoText += newLine;
            }
        }
        // 3. agent name
        if (currentJobRecord.workerName) {
            jobInfoText += format(Resources.JobInfoAgent, escape(currentJobRecord.workerName));
            jobInfoText += newLine;
        }
        // 4. start and duration
        if (start) {
            jobInfoText += format(Resources.JobInfoStart, ago(start, AgoFormat.Extended));
            jobInfoText += newLine;
            if (currentJobRecord.state === TimelineRecordState.Completed) {
                jobInfoText += format(Resources.JobInfoDuration, duration(start, currentJobRecord.end));
                jobInfoText += newLine;
            }
        }
        // 5. add job status if any
        if (jobStatus) {
            jobInfoText += newLine + jobStatus.statusMessage;
        }
        // 6. job preparation - deal with phase logs
        const phase = this._rawData.phases[this._rawData.phaseMap[currentJob.parentId]];
        if (phase && phase.logId) {
            if (this._phaseLog[phase.id]) {
                jobInfoText += newLine + groupPrefix + Resources.PrepareJob + newLine + this._phaseLog[phase.id] + newLine + endGroupPrefix;
            } else {
                // reserve some space upfront to avoid flickering when data is finally populated
                jobInfoText += newLine;
            }

            phase.logId && this._updatePhaseLog(currentJob, phase);
        }

        // 7. queue time variables if any
        if (this._rawData.parameters) {
            try {
                // JSON parse doesn't allow trailing commas, we might have them in build
                // so remove them...
                // \, - match ","
                // ?! negative look ahead (below shouldn't match)
                // \s match white space
                // *? zero or more times
                // { [ " ' \w - match characters
                const regex = /\,(?!\s*?[\{\[\"\'\w])/g;
                const params: { [key: string]: string } = JSON.parse((this._rawData.parameters || "").replace(regex, ""));
                const count = Object.keys(params).length;
                if (this._rawData.queueStatus > 0) {
                    jobInfoText += Resources.PipelinePaused;
                } else if (count > 0) {
                    const info = count > 1 ? Resources.QueueTimeVariablesGroup : Resources.QueueTimeVariableGroup;
                    const heading = format(info, count) + newLine;
                    const content: string = Object.keys(params).reduce((prev: string, key: string) => {
                        return prev + key + " : " + (params[key] || "undefined") + newLine;
                    }, "");
                    jobInfoText += newLine + groupPrefix + iconLog("Variable") + heading + content + newLine + endGroupPrefix;
                }
            } catch {
                // best effort
            }
        }

        if (this._rawData.templateParameters) {
            try {
                const params: { [key: string]: object } = this._rawData.templateParameters;
                const count = Object.keys(params).length;
                if (count > 0) {
                    const heading = format(Resources.RuntimeParameterGroup, count) + newLine;
                    const content: string = Object.keys(params).reduce((prev: string, key: string) => {
                        return prev + key + " : " + (JSON.stringify(params[key]) || "undefined") + newLine;
                    }, "");
                    jobInfoText += newLine + groupPrefix + iconLog("Variable") + heading + content + newLine + endGroupPrefix;
                }
            } catch {
                // best effort
            }
        }

        // 8. artifacts if any
        this._updateArtifacts(currentJob);
        const artifactData = this._jobArtifactData[currentJob.id];
        if (artifactData && artifactData.publishedArtifactsCount > 0) {
            const count = this._jobArtifactData[currentJob.id].publishedArtifactsCount;
            const formatText = count > 1 ? Resources.ArtifactsCount : Resources.ArtifactCount;
            // TODO: add source (job) information here when the artifacts page can understand the source and probably filter the view with that
            const artifactLink = this._linking.getBuildUrl(this._rawData.buildId, NavConstants.artifacts, undefined, undefined, {
                ["pathAsName"]: "false"
            });
            const linkSegment = linkText(artifactLink, format(formatText, count));
            const content = format(Resources.ArtifactsCountFormat, linkSegment);
            jobInfoText += newLine + iconLog("Package") + content;
        }

        // 9. test results if any
        this._updateTestData(currentJob, phase);
        const testData = this._jobTestData[currentJob.id];
        if (testData && testData.testsCount > 0) {
            const linkSegment = linkText(
                this._extService.getTestTabLink(this._rawData.buildId),
                format(
                    Resources.TestResultTestsFormat,
                    TestReportDataParser.getCustomizedDecimalValueInCurrentLocale(this._jobTestData[currentJob.id].testsPassRate, 1)
                )
            );

            const content = format(Resources.TestResultPassedFormat, linkSegment);
            jobInfoText += newLine + iconLog("TestBeaker") + content;
        }

        // last - 1: add any message
        if (currentJob.message) {
            jobInfoText += currentJob.message;
        }

        // last: add any live content
        if (this._liveContent[currentJob.id]) {
            jobInfoText += newLine + Resources.JobLiveData + newLine + this._liveContent[currentJob.id].content + newLine;
        }

        if (jobInfoText) {
            return {
                key: currentJob.id,
                title: Resources.JobInformation,
                iconProps: {
                    iconName: "Processing"
                },
                hasData: true,
                isExpanded: true,
                getData: () => {
                    return jobInfoText;
                },
                showHtml: true,
                noAutoScroll: true,
                // hidden: make this a sectionless render in the logviewer since we only have the one section
                hidden: true
            };
        }
        return null;
    }

    private _getPoolUrl(queueId: number) {
        return routeUrl(this.pageContext, "ms.vss-build-web.run-pools-route", { project: this._rawData.projectId, queueId: queueId + "" });
    }

    private _getParallelJobsUrl(isHosted: boolean, parallelismTag: string) {
        return routeUrl(this.pageContext, "ms.vss-build-web.build-queue-route", {
            project: this._rawData.projectId,
            isHosted: isHosted + "",
            parallelismTag: parallelismTag
        });
    }

    private _onSelectionChange = (newSelection: IRunSelection) => {
        // subscribe to update
        const currentJob = this._getCurrentJob(newSelection.jobId);
        if (currentJob) {
            this._updateLogsData(this._rawData.buildId, newSelection.jobId, newSelection.taskId, this._getTasks(currentJob));

            const queueData = this._rawData.jobQueueData[currentJob.id];
            if (queueData) {
                this._signalR.watchPool(queueData.poolId);
            }

            const runningSteps = this._getRunningSteps(currentJob);
            if (this._followTail && runningSteps.length > 0) {
                runningSteps.forEach(runningStep => {
                    // if found, go and grab the stored logs
                    this._fetchLinesForStep(currentJob, runningStep);
                });
            }
        }
    };

    private _fetchLinesForStep(job: RunLogsRecordData, runningStep: RunLogsRecordData, currentToken?: string) {
        this._fetchLines(job.id, runningStep.id, currentToken).then(x => {
            if (x) {
                // if this step is still running...
                const currentRunningSteps = this._getRunningSteps(job);
                const currentRunningStep = currentRunningSteps.find(step => step.id === x.stepId);
                
                let content = x.content || "";

                let prevStartLine = this._liveContent[x.stepId]?.startLine ?? Number.MAX_SAFE_INTEGER;
                if (currentRunningStep && x.timelineId === this._rawData.timelineId && x.stepId === currentRunningStep.id) {
                    // if the fetched lines already exist, skip adding them 
                    if (x.startLine >= prevStartLine) {
                        return;
                    }

                    this._liveContent[x.stepId] = {content: content + (this._liveContent[x.stepId]?.content || ""), startLine: x.startLine };
                    // update live data
                    (this._liveContentData as ObservableValue<ILiveContentData | undefined>).value = {
                        content: content,
                        jobId: job.id,
                        taskId: x.stepId,
                        timelineId: x.timelineId,
                    };

                    let sectionToUpdate: ITaskSection;
                    if (x.continuationToken) {
                        // continuationToken exists, update section data
                        sectionToUpdate = this.createTask(this._rawData.buildId, currentRunningStep);
                        sectionToUpdate.asyncInfo = {
                            getMore: (token: string) => {
                                if (!this._linesFetching[token]) {
                                    this._linesFetching[token] = true;
                                    this._fetchLinesForStep(job, runningStep, token);
                                }
                            },
                            hasMore: true,
                            payload: x.continuationToken
                        };
                    } else {
                        // no more async data left
                        sectionToUpdate = this.createTask(this._rawData.buildId, currentRunningStep);
                        sectionToUpdate.asyncInfo = undefined;
                    }

                    if (
                        this._selection.value &&
                        (this._selection.value.taskId === x.stepId || (this._selection.value.jobId === job.id && this._selection.value.taskId === ""))
                    ) {
                        // let's update the section data if the incoming live data is for the user's currently selected task or job respectively
                        const partialData: IPartialDataUpdate = {
                            updatedSections: [sectionToUpdate]
                        };
                        this._partialData = this._notifyData(this._partialData as ObservableValue<IPartialDataUpdate>, partialData);
                    }
                }
            }
        });
    }

    // for Server side jobs, they can be more than one running step...
    private _getRunningSteps(job: RunLogsRecordData): RunLogsRecordData[] {
        // grab the currently running task/step
        return this._getTasks(job, task => {
            return task.state === TimelineRecordState.InProgress;
        });
    }

    private _updateUrl(buildId: number, recordId: string, logId: number) {
        if (logId) {
            this._url[recordId] = this._getUrl(buildId, recordId, logId);
        }
    }

    private _getUrl(buildId: number, recordId: string, logId: number) {
        return routeUrl(this.pageContext, "ms.vss-build-web.run-build-logsapi-route", {
            project: this._rawData.projectId,
            buildId: buildId + "",
            logId: logId + ""
        });
    }

    private async _updateTestData(job: RunLogsRecordData, phase: RunLogsRecordData, force?: boolean) {
        if (phase && (force || (!this._jobTestData[job.id] && !this._skipTestCall[job.id]))) {
            const stage = this._rawData.stages[this._rawData.stageMap[phase.parentId]];
            try {
                const data = await this._extService.getTestsResultsForJob(this._rawData.buildId, {
                    jobName: job.refName,
                    phaseName: phase.refName,
                    stageName: stage.refName
                });
                if (data && this._selection.value) {
                    this._jobTestData[job.id] = data;
                    // update job section data if the user is currently viewing the job section
                    if (this._selection.value.jobId === job.id && this._selection.value.taskId === "") {
                        this._updateJobSection(job);
                    }
                }
                if (job.state === TimelineRecordState.Completed) {
                    // if the job is completed, irrespective of what we get from tests, we shouldn't be calling again
                    this._skipTestCall[job.id] = true;
                }
            } catch {
                // ignore, note that test API isn't available for onprem yet
                this._skipTestCall[job.id] = true;
            }
        }
    }

    private async _updatePhaseLog(job: RunLogsRecordData, phase: RunLogsRecordData) {
        if (!this._phaseLog[phase.id]) {
            if (phase && phase.logId) {
                const result = await issueRequest(this._getUrl(this._rawData.buildId, phase.id, phase.logId));
                const data = escape(await result.text());
                // strip of first and last lines, we know that it's usually ##[section] stuff
                let transformedData = data.split("\n");
                transformedData = transformedData.splice(1, transformedData.length);
                transformedData = transformedData.splice(0, transformedData.length - 2);
                this._phaseLog[phase.id] = transformedData.join("\n");
                // update job section data if the user is currently viewing the job section
                if (this._selection.value && this._selection.value.jobId === job.id && this._selection.value.taskId === "") {
                    this._updateJobSection(job);
                }
            }
        }
    }

    private async _updateArtifacts(job: RunLogsRecordData, force?: boolean) {
        if (force || (!this._jobArtifactData[job.id] && !this._skipArtifactsCall[job.id])) {
            const params: { [key: string]: string } = {};
            params[NavConstants.buildId] = this._rawData.buildId + "";
            params["source"] = job.id;
            if (job.state === TimelineRecordState.Completed) {
                // if the job is completed, irrespective of what we get, we shouldn't be calling again
                this._skipArtifactsCall[job.id] = true;
            }
            const data = await this._contributionService.getDataAsync<ArtifactsData>(
                "ms.vss-build-web.run-details-artifacts-data-provider",
                params,
                true
            );
            if (data && this._selection.value) {
                this._jobArtifactData[job.id] = data;
                // update job section data if the user is currently viewing the job section
                if (this._selection.value.jobId === job.id && this._selection.value.taskId === "") {
                    this._updateJobSection(job);
                }
            }
        }
    }

    private _updateJobSection(job: RunLogsRecordData) {
        // get new job section now
        const js = this._getJobSection(job);
        if (js) {
            const logsData: IRunLogsData = {
                buildId: this._logsData.value.buildId,
                job: this._logsData.value.job,
                stage: this._logsData.value.stage,
                message: this._logsData.value.message,
                sections: [js]
            };
            this._logsData = this._notifyData(this._logsData as ObservableValue<IRunLogsData>, logsData);
        }
    }

    private _sortRecords = (a: RunLogsRecordData, b: RunLogsRecordData) => {
        if (a.order > b.order) {
            return 1;
        }
        if (a.order < b.order) {
            return -1;
        }
        return 0;
    };

    //#region Get stuff
    private _getData = (key: string) => {
        if (!this._content[key] && this._url[key]) {
            this._content[key] = issueRequest(this._url[key]).then(result => {
                return result.text();
            });
        }
        return this._content[key];
    };

    private _getLiveData = (key: string) => {
        return this._liveContent[key]?.content;
    };

    private _getStageRecord(job: RunLogsRecordData) {
        const phaseRecord = this._rawData.phases[this._rawData.phaseMap[job.parentId]];
        const stageRef = (job as PreviousRunLogsRecordData).stageRef;
        const timelineId = (job as PreviousRunLogsRecordData).timelineId;
        if (phaseRecord) {
            return this._rawData.stages[this._rawData.stageMap[phaseRecord.parentId]];
        } else if (stageRef && timelineId) {
            const timeline = this._previousTimelines[this._previousTimelinesMap[stageRef][timelineId]];
            return timeline.stages.find(x => (x.refName = stageRef));
        }
    }
    //#endregion

    //#region Notifying observables
    /**
     * Updates and notifies logsData observable
     * @param buildId current build id
     * @param currentJobId currently selected job id
     * @param taskId: currently selected task id
     * @param tasks all tasks
     */
    private _updateLogsData(buildId: number, currentJobId: string, taskId: string, tasks: RunLogsRecordData[]) {
        const currentJob = this._getCurrentJob(currentJobId);
        if (!currentJob) {
            return;
        }

        // Scenario: A task can have phase as it's immediate parent (usually a task's immediate parent is a job), cloud build is an example of this
        const phase = this._rawData.phases[this._rawData.phaseMap[currentJob.parentId]];
        let sections: ITaskSection[] = [];
        const currentTask = this._getTaskById(taskId);
        if (!!currentTask) {
            sections.push(this.createTask(buildId, currentTask));
        } else {
            const jobSection = this._getJobSection(currentJob);
            if (jobSection) {
                sections.unshift(jobSection);
            }
        }
        const throttleMessage = this._rawData.throttleMessage;

        if (sections.length == 0) {
            sections = [
                {
                    key: "",
                    title: "",
                    hasData: true,
                    isExpanded: true,
                    // hidden: make this a sectionless render in the logviewer since we only have the one section
                    hidden: true,
                    className: "empty-sec",
                    noAutoScroll: true,
                    getData: () => {
                        if (throttleMessage) {
                            return throttleMessage;
                        } else {
                            return currentJob.result == TaskResult.Skipped
                                ? Resources.SkippedJobLogs
                                : this._rawData.queueStatus
                                ? Resources.PipelinePaused
                                : Resources.NoLogs;
                        }
                    }
                }
            ];
        }

        const stageRecord = this._getStageRecord(currentJob);
        let stage: IStageRecord | undefined;
        if (stageRecord) {
            stage = {
                ...this.createRecord(stageRecord, false, false),
                checks: stageRecord.checks || []
            };
        }

        const logsData: IRunLogsData = currentTask
            ? {
                  buildId: buildId,
                  job: this.createRecord(currentTask, false, true),
                  stage: stage,
                  sections: sections
              }
            : {
                  buildId: buildId,
                  job: this.createRecord(currentJob, true, false),
                  stage: stage,
                  sections: sections
              };

        const phaseRecord = this._getPhaseById(currentJob.parentId);

        // Check if the job version is lower than the stage version. If so, inform the user that the job ran in a previous attempt
        // If a job never runs in a previous attempt, it will have a lower attempt # but we don't want to show this message. To do this,
        // we compare the start time of the stage to the start time of the job. If the stage starts after the job, the job was copied from an
        // old stage and we show the message.
        if (
            stageRecord &&
            phaseRecord &&
            (stageRecord.attempt > phaseRecord.attempt || stageRecord.attempt > currentJob.attempt) &&
            stageRecord.startTime &&
            currentJob.startTime &&
            stageRecord.startTime > currentJob.startTime
        ) {
            logsData.message = Resources.OldJobAttempt;
        }

        this._logsData = this._notifyData(this._logsData as ObservableValue<IRunLogsData>, logsData);

        this._initJobPendingReason(currentJob);
    }

    /**
     * Updates and notifies treeData observables, this also update URL for jobs
     * @param jobs all jobs
     * @param phases all phases
     * @param stages all stages
     */
    private _updateTreeData() {
        const jobs = this._rawData.jobs;
        const phases = this._rawData.phases;
        const stages = this._rawData.stages;
        const tasks = this._rawData.tasks;

        const treeData: IRunLogsTreeData = {
            items: []
        };

        const stageIdToTreeIndex: { [key: string]: number } = {};
        const stageAttemptToIndex: { [key: string]: { [key: number]: number } } = {};

        // In a single-stage pipeline, we want to replace any default stage names with "Jobs"
        if (stages.length == 1 && stages[0].refName === "__default") {
            stages[0].name = Resources.JobsTitle;
        }

        stages.forEach((stage, index) => {
            const stageItem = {
                data: this.createRecord(stage, false, false)
            } as IRecordTreeItem;

            // Multiple attempts will have attempt nodes as childs of the stage
            if (stage.previousAttempts.length > 0) {
                stageAttemptToIndex[stage.id] = stageAttemptToIndex[stage.id] || {};

                // Push the latest attempt first
                stageItem.childItems = [];
                stageItem.childItems.push({
                    data: this.createAttemptRecord(stage.refName, format(Resources.AttemptNumber, stage.attempt), false, false)
                });
                stageAttemptToIndex[stage.id][stage.attempt] = 0;

                // Check if previous attempts have been loaded
                if (this._previousTimelinesMap[stage.refName]) {
                    // Previous attempts loaded, add attempt nodes to the stage
                    stage.previousAttempts.forEach((attempt, index) => {
                        stageItem.childItems = stageItem.childItems || [];
                        stageItem.childItems.push({
                            data: this.createAttemptRecord(stage.refName, format(Resources.AttemptNumber, attempt.attempt), false, false)
                        });
                        stageAttemptToIndex[stage.id][attempt.attempt] = index + 1;
                    });
                } else {
                    // Previous attempts not loaded, add a link for the user to load previous attempts
                    stageItem.childItems.push({
                        data: this.createActionRecord(Resources.ShowPreviousAttempts, false, false, () => {
                            Promise.resolve(this._loadAndUpdateAttempts(stage)).then(() => {
                                this._updateTreeData();
                            });
                        })
                    });
                }
            }
            treeData.items.push(stageItem);
            stageIdToTreeIndex[stage.id] = index;
        });

        // create a map from phase Id to it's stage Id
        const phaseIdToStageId: { [key: string]: string } = {};
        phases.forEach(phase => {
            phaseIdToStageId[phase.id] = phase.parentId;
        });

        const selectedJobId = this._selection && this._selection.value && this._selection.value.jobId;

        // push child items (jobs) for stages
        jobs.forEach(job => {
            this._updateUrl(this._rawData.buildId, job.id, job.logId);
            // Get the stage Id for the job
            const isCurrentJobSelected = selectedJobId === job.id;
            const stageId = phaseIdToStageId[job.parentId];
            // Get the stage index for the stage we are interested in
            const stageTreeItemIndex = stageIdToTreeIndex[stageId];
            const item: ITreeItem<IRecord> = {
                data: this.createRecord(job, true, false),
                expanded: isCurrentJobSelected
            };
            const jobTasks = tasks.filter(task => task.parentId === item.data.id);
            jobTasks.forEach(task => {
                if (item.childItems === undefined) {
                    item.childItems = [];
                }
                const taskItem: ITreeItem<IRecord> = {
                    data: this.createRecord(task, false, true),
                    expanded: false
                };
                item.childItems.push(taskItem);
                tasks.forEach(x => {
                    if (x.parentId === taskItem.data.id) {
                        if (taskItem.childItems === undefined) {
                            taskItem.childItems = [];
                        }
                        taskItem.childItems.push({
                            data: this.createRecord(x, false, true),
                            expanded: false
                        } as ITreeItem<IRecord>);
                    }
                });
            });
            if (stageTreeItemIndex !== undefined) {
                // Grab the stage so that we can push the jobs
                let stageTreeItem = treeData.items[stageTreeItemIndex];
                if (isCurrentJobSelected || !!this._expandedStages[stageId]) {
                    stageTreeItem.expanded = true;
                    this._expandedStages[stageTreeItem.data.id] = true;
                }

                // Does this stage have multiple attempts?
                if (stageId in stageAttemptToIndex) {
                    // When stages have multiple attempts, the actual node we want to add to is one level down
                    stageTreeItem.childItems = stageTreeItem.childItems || [];
                    stageTreeItem = stageTreeItem.childItems[0]; // 0 index is always latest attempt

                    // Maintain attempt expansion
                    if (isCurrentJobSelected || !!this._expandedStages[stageTreeItem.data.id]) {
                        stageTreeItem.expanded = true;
                        this._expandedStages[stageTreeItem.data.id] = true;
                    }
                }

                stageTreeItem.childItems = stageTreeItem.childItems || [];
                stageTreeItem.childItems.push(item);
            } else {
                treeData.items.push(item);
            }
        });

        // Add any additional loaded stage attempts
        stages.forEach(stage => {
            // Have the previous attempts been loaded?
            if (stage.refName in this._previousTimelinesMap) {
                for (const attempt of stage.previousAttempts) {
                    const timelineIndex = this._previousTimelinesMap[stage.refName][attempt.timelineId];
                    const timeline = this._previousTimelines[timelineIndex];
                    if (timeline !== undefined) {
                        // Add jobs from previous attempts
                        timeline.jobs.forEach(oldJob => {
                            const isCurrentJobSelected = selectedJobId === oldJob.id;
                            // Get the stage index for the stage we are interested in
                            const stageTreeItemIndex = stageIdToTreeIndex[stage.id];
                            const stageTreeItem = treeData.items[stageTreeItemIndex];

                            const attemptIndex = stageAttemptToIndex[stage.id][attempt.attempt];
                            stageTreeItem.childItems = stageTreeItem.childItems || [];
                            const attemptTreeItem = stageTreeItem.childItems[attemptIndex];

                            const oldItem: ITreeItem<IRecord> = {
                                data: this.createRecord(oldJob, true, false),
                                expanded: isCurrentJobSelected
                            };

                            // Maintain attempt expansion
                            if (isCurrentJobSelected || !!this._expandedStages[attemptTreeItem.data.id]) {
                                attemptTreeItem.expanded = true;
                                this._expandedStages[attemptTreeItem.data.id] = true;
                            }

                            const jobTasks = timeline.tasks.filter(task => task.parentId === oldItem.data.id);
                            jobTasks.forEach(task => {
                                if (oldItem.childItems === undefined) {
                                    oldItem.childItems = [];
                                }
                                const taskItem: ITreeItem<IRecord> = {
                                    data: this.createRecord(task, false, true),
                                    expanded: false
                                };
                                oldItem.childItems.push(taskItem);
                                tasks.forEach(x => {
                                    if (x.parentId === taskItem.data.id) {
                                        if (taskItem.childItems === undefined) {
                                            taskItem.childItems = [];
                                        }
                                        taskItem.childItems.push({
                                            data: this.createRecord(x, false, true),
                                            expanded: false
                                        } as ITreeItem<IRecord>);
                                    }
                                });
                            });

                            attemptTreeItem.childItems = attemptTreeItem.childItems || [];
                            attemptTreeItem.childItems.push(oldItem);
                        });
                    }
                }
            }
        });

        this._treeData = this._notifyData(this._treeData as ObservableValue<IRunLogsTreeData>, treeData);
    }

    /**
     * Updates selection from navigation and notifies selection observable
     */
    private _updateSelection = (navigationStateParam?: { [key: string]: string }) => {
        const navigationState = navigationStateParam ?? this._historyService.getState();
        let jobIdToSelect: string | undefined = navigationState[NavConstants.job] || navigationState["jobId"];
        const loadPreviousStage = navigationState[NavConstants.stage];

        if (!jobIdToSelect) {
            // no job is selected, see if stage is specified
            const stageIdToSelect = navigationState[NavConstants.stage] || "";
            if (stageIdToSelect) {
                const phase = this._rawData.phases.find(x => x.parentId === stageIdToSelect);
                if (phase) {
                    const jobFromStage = this._rawData.jobs.find(x => x.parentId === phase.id);
                    jobIdToSelect = jobFromStage && jobFromStage.id;
                }
            }
        }
        let taskId;
        if (jobIdToSelect) {
            const job = this._getJobById(jobIdToSelect);
            if (job) {
                const runningSteps = this._getRunningSteps(job);
                if (this._followTail && runningSteps && runningSteps.length > 0) {
                    runningSteps.forEach(runningStep => {
                        // if found, go and grab the stored logs
                        taskId = runningStep.id;
                    });
                }
            }
        }

        const taskToSelect = navigationState[NavConstants.task] || navigationState["taskId"] || taskId;
        let lineToSelect = parseInt(navigationState[NavConstants.line] || navigationState["line"]);

        // Extract this so we can run it later if we need to load previous timelines
        const processSelection = (updateNavigation: boolean = false) => {
            let job;
            let task;

            if (taskToSelect) {
                task = this._getTaskById(taskToSelect);
            }
            if (jobIdToSelect) {
                job = this._getJobById(jobIdToSelect);
            }
            // if we can't find job, just grab the first one
            if (!job) {
                job = this._rawData.jobs[0];
                job && this.selectJob(job.id);
                return;
            }

            if (updateNavigation) {
                if (task) {
                    this.selectTask(task.id);
                } else {
                    this.selectJob(job.id);
                }
            }

            const selection: IRunSelection = {
                jobId: job.id,
                taskId: task ? task.id : ""
            };

            if (task && task.state === TimelineRecordState.Completed && !lineToSelect && task.issues && task.issues.length) {
                lineToSelect = parseInt(task.issues[0].data && task.issues[0].data["logFileLineNumber"]);
            }

            if (taskToSelect) {
                selection.taskSelection = {
                    key: taskToSelect,
                    lineToScroll: isNaN(lineToSelect) ? undefined : lineToSelect
                };
            }

            this._selection = this._notifyData(this._selection as ObservableValue<IRunSelection>, selection);
        };

        const job = jobIdToSelect && this._getJobById(jobIdToSelect);
        if (job || !loadPreviousStage) {
            if (job && (job as PreviousRunLogsRecordData).stageId && !this.updatedSelection) {
                this.updatedSelection = true;
                processSelection(true);
                this._updateTreeData();
            } else {
                processSelection();
            }
        } else {
            // Select first job to begin with. Since we load the correct job async we need to initalize the sections
            const tempJob = this._rawData.jobs[0];
            tempJob && this.selectJob(tempJob.id);

            // If the job is from a previous unloaded timeline, load all timelines to try and find the job
            const stage = this._rawData.stages[this._rawData.stageMap[loadPreviousStage]];
            if (stage) {
                Promise.resolve(this._loadAndUpdateAttempts(stage)).then(() => {
                    // Update navigation since we set it to the default job above
                    this.updatedSelection = true;
                    processSelection(true);
                    // Since we loaded new timelines, the tree will be out of date
                    this._updateTreeData();
                });
            }
        }
    };

    /**
     * This fetches status from job request by making a call to DT, call this whenever we think the status of job might be changed
     * @param currentJob
     */
    private _initJobPendingReason(currentJob?: RunLogsRecordData) {
        currentJob = currentJob || this._getCurrentJob();
        if (!currentJob) {
            return;
        }

        const queueData = this._rawData.jobQueueData[currentJob.id];
        // Current job not running yet and we have pool information?
        if (queueData && currentJob.state !== TimelineRecordState.Completed && currentJob.state !== TimelineRecordState.InProgress) {
            const currentData = this._jobStatus[currentJob.id];
            if (currentData) {
                // we already have the request, so fetch latest
                this._dtClient.getAgentRequest(queueData.poolId, currentData.requestId, true).then(x => {
                    currentJob && this._updateJobPendingReason(currentJob, x);
                });
            } else {
                // fetch job requests for current plan
                this._dtClient.getAgentRequestsForPlan(queueData.poolId, this._rawData.planId).then(requests => {
                    // try to find the job we are interested in
                    const r = requests.find(x => x.jobId === (currentJob as RunLogsRecordData).id);
                    if (r) {
                        // found the job!
                        // fetch this job request
                        this._dtClient.getAgentRequest(queueData.poolId, r.requestId, true).then(x => {
                            // Load pool permissions (needed to check for priority bump permission)
                            const params: { [key: string]: any } = {};
                            params[NavConstants.poolId] = queueData.poolId + "";
                            this._contributionService.getDataAsync(this._poolPermissionProvider, params).then(() => {
                                currentJob && this._updateJobPendingReason(currentJob, x);
                            });
                        });
                    }
                });
            }
        }
    }

    /**
     * Updates the status message and notifies consumers about the change
     * @param currentJob
     * @param request
     */
    private _updateJobPendingReason(currentJob: RunLogsRecordData, request: TaskAgentJobRequest) {
        const actualCurrentJobId = this._selection.value && this._selection.value.jobId;
        if (currentJob.id === actualCurrentJobId) {
            // update status
            this._jobStatus[currentJob.id] = {
                poolId: request.poolId,
                requestId: request.requestId,
                matchedAgents: (request.matchedAgents || []).map(x => x.id),
                statusMessage: request.statusMessage,
                data: request.data,
                queueTime: request.queueTime,
                priority: request.priority
            };
            // get new job section if the user is viewing the job section
            if (this._selection.value && this._selection.value.jobId === currentJob.id && this._selection.value.taskId === "") {
                this._updateJobSection(currentJob);
            }
        }
    }

    private _getTasks(job?: RunLogsRecordData, filter?: (task: RunLogsRecordData) => boolean) {
        const tasks: RunLogsRecordData[] = [];
        if (!job || job.id in this._rawData.jobMap) {
            tasks.push(...this._rawData.tasks);
        }
        if (!job || !(job.id in this._rawData.jobMap)) {
            this._previousTimelines.forEach(timeline => {
                tasks.push(...timeline.tasks);
            });
        }

        const filteredTasks: RunLogsRecordData[] = [];
        const eliminatedTasks: RunLogsRecordData[] = [];
        tasks.forEach(task => {
            if ((!filter || filter(task)) && (!job || task.parentId === job.id)) {
                filteredTasks.push(task);
            } else {
                eliminatedTasks.push(task);
            }
        });

        // We gather the tasks we eliminated in the previous step to check if any were
        // actually subtasks of a task that was not filtered out. This happens in the
        // case of CloudBuild. It is a rare option that users choose to define subtasks
        // and is not natively done by our tasks so we will only check one layer of
        // subtasks. In the future if anyone chooses to have multiple layers of subtasks
        // it may become necessary to add a more robust search
        filteredTasks.forEach(t => {
            const task = eliminatedTasks.find(x => t.id === x.parentId);
            if (task) {
                filteredTasks.push(task);
            }
        });
        return filteredTasks;
    }

    private _getPhaseById(phaseId: string, checkPreviousAttempts: boolean = true) {
        if (phaseId in this._rawData.phaseMap) {
            return this._rawData.phases[this._rawData.phaseMap[phaseId]];
        }

        return checkPreviousAttempts ? this._getRecordFromPreviousTimeline(phaseId, timeline => timeline.phases) : undefined;
    }

    private _getJobById(jobId: string, checkPreviousAttempts: boolean = true) {
        if (jobId in this._rawData.jobMap) {
            return this._rawData.jobs[this._rawData.jobMap[jobId]];
        }

        return checkPreviousAttempts ? this._getRecordFromPreviousTimeline(jobId, timeline => timeline.jobs) : undefined;
    }

    private _getTaskById(taskId: string, checkPreviousAttempts: boolean = true) {
        if (taskId in this._rawData.taskMap) {
            return this._rawData.tasks[this._rawData.taskMap[taskId]];
        }
        return checkPreviousAttempts ? this._getRecordFromPreviousTimeline(taskId, timeline => timeline.tasks) : undefined;
    }

    private _getRecordFromPreviousTimeline(recordId: string, recordRetriever: (timeline: RunLogsBaseData) => RunLogsRecordData[]) {
        for (const timeline of this._previousTimelines) {
            const record = recordRetriever(timeline).find(x => x.id === recordId);
            if (record) {
                return record;
            }
        }
    }

    private _addPreviousTimeline(timeline: RunLogsBaseData, stage?: RunLogsRecordData) {
        // Don't re-add the main timeline
        if (timeline.timelineId == this._rawData.timelineId) {
            return;
        }

        stage = stage || timeline.stages[0];

        // Add timeline id and stage id for easy referencing elsewhere
        timeline.jobs.forEach(j => {
            const data = j as PreviousRunLogsRecordData;
            data.timelineId = timeline.timelineId;
            data.stageId = stage ? stage.id : undefined;
            data.stageRef = stage ? stage.refName : undefined;
        });
        timeline.tasks.forEach(t => {
            const data = t as PreviousRunLogsRecordData;
            data.timelineId = timeline.timelineId;
            data.stageId = stage ? stage.id : undefined;
            data.stageRef = stage ? stage.refName : undefined;
        });
        timeline.jobs = timeline.jobs.sort(this._sortRecords);
        timeline.tasks = timeline.tasks.sort(this._sortRecords);

        this._previousTimelinesMap[stage.refName] = this._previousTimelinesMap[stage.refName] || {};
        this._previousTimelinesMap[stage.refName][timeline.timelineId] = this._previousTimelines.push(timeline) - 1;
    }

    /**
     * This will change the observable so all consumers listening on this will gets notified
     * @param data data to notify
     * @returns observable to store back
     */
    private _notifyData<T>(data: ObservableValue<T>, newData: T): ObservableValue<T> {
        if (data) {
            data.value = newData;
        }

        return data || new ObservableValue<T>(newData);
    }
}

Services.add("IRunLogsService", { serviceFactory: RunLogsService });
